import React from 'react';
import { css } from '@compiled/react';
import { Button, ButtonAppearance } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';

import { MarketingContentBlock, AssociatedContent as AssociatedContentItem } from 'src/graphql/generated/graphql';
import { CardsList } from 'src/pages/CatalogPage/children/CardsList';
import { ResourceWithDurationAndType } from 'src/pages/CatalogPage/utils/taxonomy';

import AssociatedContent from './AssociatedContent';
import SectionHeading from './SectionHeading';

const MarketingContentBlockSection: React.FC<{
  contentBlock: MarketingContentBlock;
}> = ({ contentBlock }) => {
  const associatedContentItems = contentBlock.associatedContentResourcesCollection.items;

  const handleViewFullCatalogClick: VoidFunction = () => {
    window.location.href = '/learning/catalog?product=Rovo';
  };

  const isResourceCardSection = associatedContentItems.some(
    (associatedContent) =>
      associatedContent.__typename === 'SmLesson' ||
      associatedContent.__typename === 'SmCourse' ||
      associatedContent.__typename === 'LearningPath' ||
      associatedContent.__typename === 'Certification' ||
      associatedContent.__typename === 'Role' ||
      associatedContent.__typename === 'Topic' ||
      associatedContent.__typename === 'CertificationPrep',
  );

  return (
    <div css={sectionContainerStyles}>
      <SectionHeading
        heading={contentBlock.titleExternal}
        eyebrow={contentBlock.eyebrow}
        subtitle={contentBlock.subtitle}
        containerStyles={isResourceCardSection ? { maxWidth: '480px' } : {}}
      />
      <div
        css={[
          columnStyles,
          css({
            gridTemplateColumns: `repeat(${associatedContentItems?.length ?? 1}, 1fr)`,
            maxWidth: `${Math.min(1320, associatedContentItems?.length * 440)}px`,
            margin: '0 auto',
          }),
          !isResourceCardSection &&
            css({
              maxWidth: '1024px',
              margin: '0 auto',
            }),
        ]}
      >
        {isResourceCardSection ? (
          <CardsList
            resources={associatedContentItems as unknown as ResourceWithDurationAndType[]}
            handleCardClick={(): void => {}}
            showDescription
          />
        ) : (
          associatedContentItems.map((associatedContent) => (
            <AssociatedContent
              key={associatedContent.sys.id}
              associatedContent={associatedContent as AssociatedContentItem}
            />
          ))
        )}
      </div>
      {isResourceCardSection && (
        <div css={buttonContainerStyles}>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            label="Explore full catalog"
            onClick={handleViewFullCatalogClick}
          />
        </div>
      )}
    </div>
  );
};

const sectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),
});

const columnStyles = css({
  display: 'grid',
  gap: token('space.300'),
  padding: `0 ${token('space.300')}`,

  [media.below.md]: {
    gridTemplateColumns: `repeat(1, 1fr)`,
    padding: 0,
  },

  '& [data-testid="learning-content-card"]': {
    // override card to fill space when other cards are taller
    height: '100%',
  },
});

const buttonContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
});

export default MarketingContentBlockSection;
